import {
  getDashboardUsers,
  getDashboardOrganizations,
  getDashboardCredentials,
  getDashboardNotifications,
  exportDashboard
} from '@/services/adminDashboardService'
import type {
  DashboardUserResponse,
  DashboardOrganizationResponse,
  DashboardCredentialResponse,
  DashboardNotificationLogResponse,
  DashboardExportType
} from '@/types/adminDashboard'
import React from 'react'
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatCurrency } from '@/helpers/billing'
import { formatDateTimeSafe } from '@/pages/Dashboard/helpers/dateRange'
import { getLast30Days, toLocalDateString } from '@/pages/Dashboard/helpers/dateRange'
import { OrganizationDetailModal } from '@/pages/Dashboard/components/OrganizationDetailModal'
import { NotificationDetailModal } from '@/pages/Dashboard/components/NotificationDetailModal'
import { Download, Braces } from 'lucide-react'
import { TableIcon } from '@/components/custom/Table/TableIcon'
import { Pagination } from '@/components/custom/Pagination/Pagination'
import { DestinationSelectInputField } from '@/components/custom/InputField/WorkflowSelectInput'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'

const DEFAULT_LIMIT = 10
const TAB_IDS = ['organizations', 'users', 'credentials', 'notifications'] as const
type TabId = (typeof TAB_IDS)[number]

const TAB_LABELS: Record<TabId, string> = {
  organizations: 'Organizations',
  users: 'Users',
  credentials: 'Credentials',
  notifications: 'Notifications',
}

function isTabId(value: string): value is TabId {
  return (TAB_IDS as readonly string[]).includes(value)
}

/** Notification event type codes for filter. */
const NOTIFICATION_EVENT_TYPES: { value: string; label: string }[] = [
  { value: 'all', label: 'All event types' },
  { value: 'workflow_run_manual_failed', label: 'Manual workflow run failed' },
  { value: 'workflow_run_scheduled_failed', label: 'Scheduled workflow run failed' }
]

function formatCredentialExpirationStatus(row: DashboardCredentialResponse): string {
  const days = row.daysUntilExpiry
  if (days !== undefined) {
    if (days > 0) return `Will expire in ${days} day${days === 1 ? '' : 's'}`
    if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
    return 'Expires today'
  }
  const expiresAt = new Date(row.expiresAt)
  if (Number.isNaN(expiresAt.getTime())) return '—'
  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()
  const diffDays = Math.trunc(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays > 0) return `Will expire in ${diffDays} day${diffDays === 1 ? '' : 's'}`
  if (diffDays < 0) return `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'} ago`
  return 'Expires today'
}

// ── Style constants ────────────────────────────────────────────────────────────

const thClass = 'text-left px-4 py-[9px] text-[11px] font-semibold uppercase tracking-[0.05em] text-secondary border-b border-border whitespace-nowrap'
const tdClass = 'text-left px-4 py-[11px] border-b border-slate-100 dark:border-slate-800 align-middle text-[13px] text-card-foreground'

// ── Component ─────────────────────────────────────────────────────────────────

export const DashboardDetailTabs = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { token } = useSelector((state: RootState) => state.user)
  const { applications } = useSelector((state: RootState) => state.application)
  const { startDate: defaultStart, endDate: defaultEnd } = getLast30Days()
  const defaultStartDate = toLocalDateString(new Date(defaultStart))
  const defaultEndDate = toLocalDateString(new Date(defaultEnd))

  const [activeTab, setActiveTab] = useState<TabId>('organizations')
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState(defaultEndDate)
  const [organizationId, setOrganizationId] = useState('')
  const [status, setStatus] = useState('')
  const [eventTypeCode, setEventTypeCode] = useState('')
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [selectedNotificationId, setSelectedNotificationId] = useState<string | null>(null)
  const [users, setUsers] = useState<DashboardUserResponse[]>([])
  const [organizations, setOrgs] = useState<DashboardOrganizationResponse[]>([])
  const [credentialsItems, setCredentialsItems] = useState<DashboardCredentialResponse[]>([])
  const [credentialsTotal, setCredentialsTotal] = useState(0)
  const [notifications, setNotifications] = useState<DashboardNotificationLogResponse[]>([])

  const handleTabChange = useCallback((v: TabId) => {
    setActiveTab(v)
    setOffset(0)
  }, [])

  const startISO = startDate ? new Date(`${startDate}T00:00:00`).toISOString() : undefined
  const endISO = endDate ? new Date(`${endDate}T23:59:59`).toISOString() : undefined

  const fetchUsers = useCallback(async () => {
    if (!token) return
    setLoading(true)
    const resp = await dispatch(
      getDashboardUsers(token, {
        startDate: startISO, endDate: endISO,
        organizationId: organizationId || undefined,
        limit: DEFAULT_LIMIT, offset
      }, false)
    )
    setLoading(false)
    if (resp.data) setUsers(resp.data)
  }, [token, dispatch, startISO, endISO, organizationId, offset])

  const fetchOrganizations = useCallback(async () => {
    if (!token) return
    setLoading(true)
    const resp = await dispatch(
      getDashboardOrganizations(token, { startDate: startISO, endDate: endISO, limit: DEFAULT_LIMIT, offset }, false)
    )
    setLoading(false)
    if (resp.data) setOrgs(resp.data)
  }, [token, dispatch, startISO, endISO, offset])

  const fetchCredentials = useCallback(async () => {
    if (!token) return
    setLoading(true)
    const resp = await dispatch(
      getDashboardCredentials(token, { limit: DEFAULT_LIMIT, offset }, false)
    )
    setLoading(false)
    if (resp.data) {
      setCredentialsItems(resp.data.items)
      setCredentialsTotal(resp.data.total)
    }
  }, [token, dispatch, offset])

  const fetchNotifications = useCallback(async () => {
    if (!token) return
    setLoading(true)
    const resp = await dispatch(
      getDashboardNotifications(token, {
        startDate: startISO, endDate: endISO,
        eventTypeCode: eventTypeCode || undefined,
        status: status || undefined,
        organizationId: organizationId || undefined,
        limit: DEFAULT_LIMIT, offset
      }, false)
    )
    setLoading(false)
    if (resp.data) setNotifications(resp.data)
  }, [token, dispatch, startISO, endISO, organizationId, status, eventTypeCode, offset])

  useEffect(() => {
    if (!token) return
    switch (activeTab) {
      case 'users': fetchUsers(); break
      case 'organizations': fetchOrganizations(); break
      case 'credentials': fetchCredentials(); break
      case 'notifications': fetchNotifications(); break
    }
  }, [activeTab, token, fetchUsers, fetchOrganizations, fetchCredentials, fetchNotifications])

  const handleExport = async (type: DashboardExportType, format: 'csv' | 'json') => {
    if (!token) return
    await dispatch(
      exportDashboard(token, {
        type, format,
        startDate: startISO, endDate: endISO,
        organizationId: organizationId || undefined,
        status: status || undefined,
        eventTypeCode: type === 'notifications' ? (eventTypeCode || undefined) : undefined,
        limit: 5000, offset: 0
      })
    )
  }

  const exportTypeForTab: Record<TabId, DashboardExportType> = {
    organizations: 'organizations',
    users: 'users',
    credentials: 'credentials',
    notifications: 'notifications'
  }

  const currentPageCount =
    activeTab === 'users' ? users.length
    : activeTab === 'organizations' ? organizations.length
    : activeTab === 'credentials' ? credentialsItems.length
    : notifications.length
  const hasNextPage =
    activeTab === 'credentials'
      ? offset + credentialsItems.length < credentialsTotal
      : currentPageCount >= DEFAULT_LIMIT
  const nextDisabled = loading || !hasNextPage

  const renderFilters = (
    showOrg: boolean,
    showStatus: boolean,
    showExport: boolean,
    showDateRange = true
  ) => (
    <div className='p-4 flex items-center justify-between w-full'>
      <div className='flex flex-col items-start gap-2'>
        <p className='text-sm text-primary font-semibold'>Filter</p>
        <div className='flex gap-2'>
          {showDateRange && (
            <div className="flex gap-2">
              <div className='flex flex-col items-start'>
                <Label htmlFor="dashboard-start" className="text-sm mb-1">Start date</Label>
                <Input
                  id="dashboard-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36 text-primary"
                  aria-label="Start date for filtering"
                />
              </div>
              <div className='flex flex-col items-start'>
                <Label htmlFor="dashboard-end" className="text-sm mb-1">End date</Label>
                <Input
                  id="dashboard-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36 text-primary"
                  aria-label="End date for filtering"
                />
              </div>
            </div>
          )}
          {showOrg && (
            <div className='flex flex-col items-start'>
              <Label htmlFor="dashboard-org" className="text-sm mb-1">Organization ID</Label>
              <Input
                id="dashboard-org"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                placeholder="Optional"
                className="w-64 text-primary"
              />
            </div>
          )}
          {activeTab === 'notifications' && (
            <>
              <div className='flex flex-col items-start'>
                <Label htmlFor="dashboard-event-type" className="text-sm mb-1">Event type</Label>
                <DestinationSelectInputField
                  value={eventTypeCode || 'all'}
                  selectOptions={NOTIFICATION_EVENT_TYPES.map(({ value, label }) => ({ id: value, value: label }))}
                  onChange={(value) => setEventTypeCode(value === 'all' ? '' : value)}
                  additionalClassName="w-56"
                />
              </div>
              {showStatus && (
                <div className='flex flex-col items-start'>
                  <Label htmlFor="dashboard-notif-status" className='text-sm mb-1'>Status</Label>
                  <Input
                    id="dashboard-notif-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    placeholder="e.g. sent"
                    className="w-32 text-primary"
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div className='flex flex-col items-start gap-2'>
        <div className='h-6' />
        {showExport && (
          <div className='flex flex-col items-start'>
            <Label htmlFor="dashboard-notif-status" className='text-sm mb-1'>Download</Label>
            <div className='flex gap-2'>
              <Button
                variant="outline"
                size="sm"
                className='border border-accent text-accent bg-[#e9effd]'
                onClick={() => handleExport(exportTypeForTab[activeTab], 'csv')}
              >
                <Download className="mr-1 h-4 w-4" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className='border border-success text-success bg-success-foreground'
                onClick={() => handleExport(exportTypeForTab[activeTab], 'json')}
              >
                <Braces className="mr-1 h-4 w-4" />
                JSON
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )

  const renderTable = (headers: string[], rows: React.ReactNode, colCount: number) => {
    const headerColCount = headers.length
    const spanColCount = Math.max(headerColCount, colCount)
    const skeletonWidths = ['w-40', 'w-32', 'w-28', 'w-36', 'w-24', 'w-44']

    return (
      <div style={{ overflowX: 'auto' }}>
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {headers.map((h) => <th key={h} className={thClass}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, rowIdx) => (
                <tr key={`skeleton-row-${rowIdx}`}>
                  {Array.from({ length: headerColCount }).map((__, colIdx) => (
                    <td key={`skeleton-cell-${rowIdx}-${colIdx}`} className={tdClass}>
                      <Skeleton className={`h-4 ${skeletonWidths[colIdx % skeletonWidths.length]}`} />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              rows ?? (
                <tr>
                  <td colSpan={spanColCount} className={`${tdClass} text-center text-slate-400`}>
                    No data
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    )
  }

  const paginationTotal =
    activeTab === 'credentials'
      ? credentialsTotal
      : nextDisabled
        ? offset + currentPageCount
        : offset + DEFAULT_LIMIT + 1

  const handleBottomPageChange = (newOffset: number) => {
    if (loading) return
    if (newOffset > offset && nextDisabled) return
    setOffset(newOffset)
  }

  return (
    <Tabs
      value={activeTab}
      onValueChange={(value) => {
        if (!isTabId(value)) return
        handleTabChange(value)
      }}
      className="bg-card rounded-md overflow-hidden"
    >
      <TabsList className="flex w-full h-12 bg-transparent">
        {TAB_IDS.map((id) => (
          <TabsTrigger
            key={id}
            value={id}
            className="
              relative px-4 h-12 text-sm font-medium text-secondary
              transition-colors duration-200
              hover:text-slate-800
              data-[state=active]:text-primary data-[state=active]:font-semibold
              after:absolute after:left-0 after:bottom-0 after:h-[1px] after:w-0
              after:bg-blue-600 after:transition-all after:duration-300
              data-[state=active]:after:w-full
            "
          >
            {TAB_LABELS[id]}
          </TabsTrigger>
        ))}
      </TabsList>
      <Separator />

      <TabsContent value="users" className="mt-0">
        {renderFilters(true, false, true)}
        {renderTable(
          ['Email', 'Name', 'Signup date', 'Organization'],
          users.length === 0 ? null : <>
            {users.map((u) => (
              <tr key={u.userId} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                <td className={tdClass}>{u.userEmail}</td>
                <td className={tdClass}>{[u.firstName, u.lastName].filter(Boolean).join(' ')}</td>
                <td className={`${tdClass} whitespace-nowrap`}>{formatDateTimeSafe(u.signupDate)}</td>
                <td className={tdClass}>{u.organizationName ?? '—'}</td>
              </tr>
            ))}
          </>,
          4
        )}
        <div className="px-4 pb-4">
          <Pagination
            total={paginationTotal}
            limit={DEFAULT_LIMIT}
            offset={offset}
            onPageChange={handleBottomPageChange}
            onLimitChange={() => {}}
            showFirstLast={false}
            showPageCount={true}
            showPageSizeSelect={false}
            showSummary={true}
          />
        </div>
      </TabsContent>

      <TabsContent value="organizations" className="mt-0">
        {renderFilters(false, false, true)}
        {renderTable(
          ['Name', 'Created', 'Outstanding payments', 'MRR', 'Playbook count'],
          organizations.length === 0 ? null : <>
            {organizations.map((o) => {
              const playbookCount = o.subscriptions.reduce((sum, s) => sum + (s.playbookCount ?? 0), 0)
              return (
                <tr
                  key={o.organizationId}
                  className="hover:bg-slate-50 dark:hover:bg-slate-900"
                >
                  <td className={`${tdClass} font-medium`}>
                    <button
                      aria-label={`View organization ${o.organizationName}`}
                      className="text-left w-full bg-transparent border-none p-0 cursor-pointer font-medium text-slate-900 hover:text-blue-600"
                      onClick={() => setSelectedOrgId(o.organizationId)}
                    >
                      {o.organizationName}
                    </button>
                  </td>
                  <td className={`${tdClass} whitespace-nowrap`}>{formatDateTimeSafe(o.createdAt)}</td>
                  <td className={tdClass}>{formatCurrency(o.outstandingPaymentsAmountCents / 100)}</td>
                  <td className={tdClass}>{formatCurrency(o.mrrCents / 100)}</td>
                  <td className={tdClass}>{o.subscriptions.length === 0 ? '—' : playbookCount}</td>
                </tr>
              )
            })}
          </>,
          5
        )}
        <div className="px-4 pb-4">
          <Pagination
            total={paginationTotal}
            limit={DEFAULT_LIMIT}
            offset={offset}
            onPageChange={handleBottomPageChange}
            onLimitChange={() => {}}
            showFirstLast={false}
            showPageCount={true}
            showPageSizeSelect={false}
            showSummary={true}
          />
        </div>
      </TabsContent>

      <TabsContent value="credentials" className="mt-0">
        <div className='p-4  flex flex-col items-start'>
          <p className='text-sm text-primary font-semibold'>Credentials expiring or expired</p>
          <p className='text-xs text-secondary'>Credentials that expire within 30 days or have already expired (for apps that support expiration)</p>
        </div>
        {renderTable(
          ['App', 'Organization', 'Credential', 'When it expires'],
          credentialsItems.length === 0 ? null : <>
            {credentialsItems.map((row) => (
              <tr key={row.credentialId} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                <td className={tdClass}>
                  {applications.find((a) => a.displayName === row.appName)?.logo ?
                    <TableIcon iconUrl={applications.find((a) => a.displayName === row.appName)?.logo ?? ''} />
                    : row.appName}
                </td>
                <td className={tdClass}>{row.organizationName ?? row.organizationId}</td>
                <td className={`${tdClass} flex flex-col items-start`}>
                  {row.credentialName}
                  <span className='text-xs text-secondary'>{row.credentialId}</span>
                </td>
                <td className={`${tdClass} whitespace-nowrap`}>
                  {formatDateTimeSafe(row.expiresAt)} - {formatCredentialExpirationStatus(row)}
                </td>
              </tr>
            ))}
          </>,
          5
        )}
        <div className="px-4 pb-4">
          <Pagination
            total={paginationTotal}
            limit={DEFAULT_LIMIT}
            offset={offset}
            onPageChange={handleBottomPageChange}
            onLimitChange={() => {}}
            showFirstLast={false}
            showPageCount={true}
            showPageSizeSelect={false}
            showSummary={true}
          />
        </div>
      </TabsContent>

      <TabsContent value="notifications" className="mt-0">
        {renderFilters(true, true, true)}
        {renderTable(
          ['Event', 'Channel', 'Recipient', 'Status', 'Created'],
          notifications.length === 0 ? null : <>
            {notifications.map((n) => (
              <tr
                key={n.id}
                className="hover:bg-slate-50 dark:hover:bg-slate-900"
              >
                <td className={tdClass}>
                  <button
                    aria-label={`View notification ${n.eventTypeCode}`}
                    className="text-left w-full bg-transparent border-none p-0 cursor-pointer text-slate-900 hover:text-blue-600"
                    onClick={() => setSelectedNotificationId(n.id)}
                  >
                    {n.eventTypeCode}
                  </button>
                </td>
                <td className={tdClass}>{n.channel}</td>
                <td className={tdClass}>{n.recipient}</td>
                <td className={tdClass}>{n.status}</td>
                <td className={`${tdClass} whitespace-nowrap`}>{formatDateTimeSafe(n.dateCreated)}</td>
              </tr>
            ))}
          </>,
          5
        )}
        <div className="px-4 pb-4">
          <Pagination
            total={paginationTotal}
            limit={DEFAULT_LIMIT}
            offset={offset}
            onPageChange={handleBottomPageChange}
            onLimitChange={() => {}}
            showFirstLast={false}
            showPageCount={true}
            showPageSizeSelect={false}
            showSummary={true}
          />
        </div>
      </TabsContent>

      <OrganizationDetailModal
        open={selectedOrgId !== null}
        onOpenChange={(open) => !open && setSelectedOrgId(null)}
        organizationId={selectedOrgId}
        token={token ?? null}
      />
      <NotificationDetailModal
        open={selectedNotificationId !== null}
        onOpenChange={(open) => !open && setSelectedNotificationId(null)}
        notificationId={selectedNotificationId}
        token={token ?? null}
      />
    </Tabs>
  )
}
